import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SchemaMarkup from "@/components/SchemaMarkup";
import SEOMeta from "@/components/SEOMeta";
import LocalBusinessSchema from "@/components/LocalBusinessSchema";
import FAQ from "@/components/FAQ";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { Hammer, Home, Building, Wrench, MapPin, Phone, Mail, CheckCircle, Star, Users, Award } from "lucide-react";

// Import carpenter images
import carpenterFramework from "@/assets/carpenter-framework.jpg";
import carpenterCabinetry from "@/assets/carpenter-cabinetry.jpg";
import carpenterTrim from "@/assets/carpenter-trim.jpg";
import carpenterDeck from "@/assets/carpenter-deck.jpg";

const CarpenterServices = () => {
  const faqData = [
    {
      question: "Do you provide carpenter services in Niagara Falls and St. Catharines?",
      answer: "Yes! Blue Knight Contracting provides professional carpenter and carpentry services throughout the Niagara region including Niagara Falls, St. Catharines, Welland, Port Colborne, and Niagara-on-the-Lake. Our skilled carpenters handle custom cabinetry, framing, trim work, and all carpentry needs."
    },
    {
      question: "Are your carpenters licensed in Toronto and Hamilton?",
      answer: "Absolutely. Our carpenters are fully licensed, insured, and experienced in serving Toronto, Hamilton, and surrounding areas. We specialize in both residential and commercial carpentry services with proper permits and certifications."
    },
    {
      question: "Can you handle both rough carpentry and finish carpentry?",
      answer: "Yes. Our skilled carpenters specialize in both rough carpentry (framing, structural work, floor systems) and finish carpentry (trim, crown molding, custom cabinetry, built-ins, wainscoting)."
    },
    {
      question: "Do you offer emergency carpenter services in Niagara and Hamilton?",
      answer: "We provide priority carpentry services for urgent repairs including structural issues, water damage repairs, and storm damage throughout Niagara region, Hamilton, and the Greater Toronto Area."
    },
    {
      question: "What carpentry services do you offer for kitchen and bathroom renovations?",
      answer: "Our carpenters specialize in custom kitchen cabinets, vanities, countertop installation, flooring, trim work, and complete carpentry for bathroom and kitchen remodels in Niagara, Toronto, and Hamilton."
    },
    {
      question: "Do you build custom decks and outdoor structures?",
      answer: "Yes! We build custom decks, pergolas, gazebos, fences, and outdoor carpentry structures using premium materials designed for Ontario weather conditions."
    }
  ];

  const serviceData = {
    "@type": "Service",
    name: "Carpenter Services Ontario",
    description: "Professional carpentry services across Ontario including framing, finish carpentry, custom cabinetry, and millwork for residential and commercial projects.",
    provider: {
      "@type": "Organization",
      name: "Blue Knight Contracting and Construction"
    },
    serviceType: "Carpentry Services",
    areaServed: "Ontario, Canada"
  };

  const carpentryServices = [
    {
      title: "Framing & Structural Work",
      description: "House framing, floor joists, roof trusses, and structural carpentry for new construction and renovations.",
      icon: Home
    },
    {
      title: "Custom Cabinetry",
      description: "Kitchen cabinets, bathroom vanities, built-in storage solutions, and custom millwork designed to your specifications.",
      icon: Building
    },
    {
      title: "Trim & Finish Carpentry",
      description: "Crown molding, baseboards, door and window casings, wainscoting, and detailed finish work.",
      icon: Wrench
    },
    {
      title: "Flooring Installation",
      description: "Hardwood flooring, laminate, engineered wood, and subfloor preparation and installation.",
      icon: CheckCircle
    },
    {
      title: "Deck & Outdoor Structures",
      description: "Custom decks, pergolas, gazebos, fencing, and outdoor carpentry projects using weather-resistant materials.",
      icon: Star
    },
    {
      title: "Repairs & Renovations",
      description: "Carpentry repairs, renovations, additions, and restoration work for residential and commercial properties.",
      icon: Hammer
    }
  ];

  const primaryServiceAreas = [
    {
      region: "Niagara Region",
      cities: ["Niagara Falls", "St. Catharines", "Welland", "Port Colborne", "Niagara-on-the-Lake", "Fort Erie", "Grimsby", "Thorold"],
      link: "/locations"
    },
    {
      region: "Greater Toronto Area",
      cities: ["Toronto", "Mississauga", "Brampton", "Vaughan", "Markham", "Richmond Hill", "Oakville", "Burlington"],
      link: "/locations/toronto"
    },
    {
      region: "Hamilton & Area",
      cities: ["Hamilton", "Stoney Creek", "Ancaster", "Dundas", "Waterdown", "Burlington"],
      link: "/locations/hamilton"
    }
  ];

  const additionalServiceAreas = [
    "Kitchener", "Waterloo", "Cambridge", "Guelph", "London", "Ottawa", "Kingston"
  ];

  return (
    <>
      <SEOMeta 
        title="Carpentry & Woodworking Contractor — Decks, Fences, Framing | Niagara • Hamilton • GTA"
        description="Expert carpentry & woodworking services in Niagara, Hamilton & the GTA. Custom cabinetry, decks, fences & framing by trusted local contractors."
        keywords="carpentry contractor Niagara, Hamilton carpentry services, GTA woodworking, decks, fences, cabinetry, framing, residential carpentry"
        canonicalUrl="https://blueknightcontracting.ca/services/carpenter"
        ogImage={carpenterCabinetry}
        schemaType="Service"
        schemaData={{
          ...serviceData,
          mainEntity: {
            "@type": "FAQPage",
            mainEntity: faqData.map(faq => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer
              }
            }))
          }
        }}
      />
      
      <LocalBusinessSchema 
        locationName="Niagara Region"
        addressLocality="Niagara Falls"
        addressRegion="ON"
        postalCode="L2E"
        description="Professional carpenter and carpentry services in Niagara region, Toronto, and Hamilton. Our licensed carpenters specialize in custom cabinetry, framing, finish carpentry, deck building, and complete carpentry solutions for residential and commercial projects."
        services={[
          "Custom Carpentry Services",
          "Framing & Structural Carpentry",
          "Finish Carpentry & Trim Work",
          "Custom Cabinetry & Millwork",
          "Deck & Outdoor Structures",
          "Kitchen & Bathroom Carpentry",
          "Hardwood Flooring Installation",
          "Commercial Carpentry",
          "Renovation Carpentry",
          "Emergency Carpentry Repairs"
        ]}
      />
      
      <div className="min-h-screen bg-background">
        <Header />
        
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary to-primary/80 text-white py-20">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl lg:text-6xl font-bold mb-6">
                  Professional Carpenter Services Niagara, Toronto & Hamilton
                </h1>
                <p className="text-xl mb-8 opacity-90">
                  Blue Knight Contracting provides expert carpentry services throughout Niagara region, Toronto, and Hamilton. Our licensed carpenters specialize in custom cabinetry, framing, finish carpentry, decks, and complete carpentry solutions for residential and commercial projects.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button asChild size="lg" variant="secondary">
                    <Link to="/contact">Get Free Quote</Link>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="text-white bg-primary border-white hover:bg-white hover:text-primary">
                    <Link to="/services">View All Services</Link>
                  </Button>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={carpenterCabinetry} 
                  alt="Ontario carpenter creating custom wooden cabinetry with precision craftsmanship"
                  className="rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Expert Carpentry Services for Niagara, Toronto & Hamilton
              </h2>
              <p className="text-xl text-foreground/70 max-w-3xl mx-auto">
                Our licensed carpenters in Niagara Falls, St. Catharines, Toronto, and Hamilton handle everything from structural framing to custom finish carpentry, delivering precision craftsmanship for homes and businesses across Southern Ontario.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {carpentryServices.map((service, index) => (
                <Card key={index} className="h-full">
                  <CardHeader>
                    <service.icon className="h-12 w-12 text-primary mb-4" />
                    <CardTitle>{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Internal Links to Related Services */}
            <div className="mt-16 text-center">
              <h3 className="text-2xl font-bold mb-6">Related Construction Services</h3>
              <div className="flex flex-wrap justify-center gap-4">
                <Button asChild variant="outline">
                  <Link to="/services/kitchen-renovations">Kitchen Renovations</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/services/bathroom-renovations">Bathroom Renovations</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/services/flooring">Flooring Installation</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/services/general-contracting">General Contracting</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/locations/toronto">Toronto Services</Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/locations/hamilton">Hamilton Services</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Projects */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Featured Carpentry Projects
              </h2>
              <p className="text-xl text-foreground/70 max-w-3xl mx-auto">
                Explore our portfolio of custom carpentry work across Ontario
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={carpenterFramework} 
                    alt="Ontario carpenter building custom wooden framework for residential construction"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Structural Framing</h3>
                  <p className="text-muted-foreground">
                    Precision framing for new construction and additions throughout the GTA
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={carpenterTrim} 
                    alt="Ontario carpenter installing wooden trim and molding in residential home interior"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Custom Trim Work</h3>
                  <p className="text-muted-foreground">
                    Detailed trim and molding installation for elegant interior finishes
                  </p>
                </CardContent>
              </Card>

              <Card className="overflow-hidden">
                <div className="aspect-[4/3]">
                  <img 
                    src={carpenterDeck} 
                    alt="Carpenter building custom wooden deck and outdoor structure in Ontario"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-2">Outdoor Structures</h3>
                  <p className="text-muted-foreground">
                    Custom decks, pergolas, and outdoor carpentry projects
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Why Choose Blue Knight Carpentry?
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center p-6">
                <Users className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Skilled Craftsmen</h3>
                <p className="text-muted-foreground">
                  Experienced carpenters with expertise in all aspects of woodworking
                </p>
              </Card>

              <Card className="text-center p-6">
                <Award className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Quality Materials</h3>
                <p className="text-muted-foreground">
                  Premium lumber and hardware for lasting results
                </p>
              </Card>

              <Card className="text-center p-6">
                <CheckCircle className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Custom Solutions</h3>
                <p className="text-muted-foreground">
                  Tailored carpentry work to meet your specific needs
                </p>
              </Card>

              <Card className="text-center p-6">
                <Star className="h-12 w-12 text-primary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Ontario Wide</h3>
                <p className="text-muted-foreground">
                  Serving communities across Ontario from GTA to Northern regions
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-20 bg-muted/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Local Carpenter Services - Niagara, Toronto & Hamilton
              </h2>
              <p className="text-xl text-foreground/70 max-w-3xl mx-auto">
                Professional carpentry services throughout Southern Ontario. Our local carpenters are ready to help with your project.
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8 mb-12">
              {primaryServiceAreas.map((area, index) => (
                <Card key={index} className="p-6">
                  <div className="flex items-start gap-3 mb-4">
                    <MapPin className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-bold mb-3">{area.region} Carpenters</h3>
                      <div className="space-y-2">
                        {area.cities.map((city, cityIndex) => (
                          <p key={cityIndex} className="text-muted-foreground">✓ {city}</p>
                        ))}
                      </div>
                      <Button asChild variant="link" className="mt-4 px-0">
                        <Link to={area.link}>View {area.region} Services →</Link>
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            <div className="text-center">
              <h3 className="text-2xl font-bold mb-6">We Also Serve</h3>
              <div className="flex flex-wrap justify-center gap-4">
                {additionalServiceAreas.map((area, index) => (
                  <Card key={index} className="px-6 py-3">
                    <p className="font-medium">{area}</p>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Carpenter Services FAQ - Niagara, Toronto & Hamilton
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Common questions about our professional carpentry services in Niagara region, Toronto, and Hamilton
              </p>
            </div>

            <div className="max-w-4xl mx-auto space-y-8">
              {faqData.map((faq, index) => (
                <Card key={index} className="p-6">
                  <h3 className="text-xl font-semibold mb-3 text-primary">
                    {faq.question}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-primary text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">
              Need a Skilled Carpenter in Niagara, Toronto, or Hamilton?
            </h2>
            <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
              Contact Blue Knight Contracting for professional carpentry services in Niagara Falls, St. Catharines, Toronto, and Hamilton. Our licensed carpenters deliver quality craftsmanship. Get your free quote today!
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <div className="flex items-center gap-2">
                <Phone className="h-5 w-5" />
                <span>Call: [Insert Phone Number]</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                <span>Email: [Insert Email]</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                <span>Serving all of Ontario</span>
              </div>
            </div>
            
            <Button asChild size="lg" variant="secondary">
              <Link to="/contact">Get Free Quote Today</Link>
            </Button>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default CarpenterServices;